import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '../components/AppLayout';
import api from '../api/axiosInstance';
import './timetrendshift.css';

/* ─── Constants ──────────────────────────────────────────────────── */
const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];
const curYear  = new Date().getFullYear();
const curMonth = new Date().getMonth() + 1;
const YEAR_OPTIONS = Array.from({ length: 8 }, (_, i) => curYear - 2 + i);
const HEADER_H = 52;

function isTradingDay(dateStr) {
  return ['Monday','Tuesday','Wednesday','Thursday','Friday']
    .some((d) => dateStr?.includes(d));
}

/* ─── Stars (generated once) ─────────────────────────────────────── */
const STARS = Array.from({ length: 70 }, (_, i) => ({
  id: i,
  left:  `${Math.random() * 100}%`,
  top:   `${Math.random() * 100}%`,
  delay: `${(Math.random() * 4).toFixed(2)}s`,
  size:  Math.random() > 0.8 ? 3 : 2,
}));

/* ─── BhadraCard ─────────────────────────────────────────────────── */
function BhadraCard({ item, index }) {
  return (
    <div className="tts-card" style={S.card}>
      <div className="tts-card-top" style={S.cardTop}>
        <span className="tts-card-badge" style={S.cardBadge}>🌙 #{index + 1}</span>
        <span className="tts-card-duration" style={S.cardDur}>⏱ {item.durationHrs}h {item.durationMins}m</span>
      </div>
      <div className="tts-time-split" style={S.timeSplit}>
        <div className="tts-time-block" style={{ ...S.timeBlock, borderLeft: '3px solid #ef4444' }}>
          <div className="tts-time-label" style={S.timeLabel}><span style={{ color: '#ef4444' }}>▶</span> BEGINS</div>
          <div className="tts-time-value" style={S.timeVal}>{item.start}</div>
        </div>
        <div className="tts-time-block" style={{ ...S.timeBlock, borderLeft: '3px solid #10b981' }}>
          <div className="tts-time-label" style={S.timeLabel}><span style={{ color: '#10b981' }}>■</span> ENDS</div>
          <div className="tts-time-value" style={S.timeVal}>
            {item.end}
            {item.continues && <div className="tts-cont-badge" style={S.contBadge}>↪ Continues next month</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── InfoPill ───────────────────────────────────────────────────── */
function InfoPill({ icon, value, label, color }) {
  return (
    <div className="tts-pill" style={{ ...S.pill, borderColor: color + '44' }}>
      <span className="tts-pill-dot" style={{ ...S.pillDot, background: color }}>{icon}</span>
      <div>
        <div className="tts-pill-value" style={{ fontSize: '1rem', fontWeight: 700, lineHeight: 1.1, color: '#fff' }}>{value}</div>
        <div className="tts-pill-label" style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: 0.7 }}>{label}</div>
      </div>
    </div>
  );
}

/* ─── Main Component ─────────────────────────────────────────────── */
export default function TimeTrendShift() {
  const [month, setMonth]             = useState(curMonth);
  const [year, setYear]               = useState(curYear);
  const [tradingOnly, setTradingOnly] = useState(false);
  const [showAbout, setShowAbout]     = useState(false);
  const [loading, setLoading]         = useState(false);
  const [spinning, setSpinning]       = useState(false);
  const [error, setError]             = useState('');
  const [allData, setAllData]         = useState([]);
  const [statusType, setStatusType]   = useState('idle');

  const displayed = tradingOnly
    ? allData.filter((b) => isTradingDay(b.start))
    : allData;

  const fetchBhadra = useCallback(async (m, y) => {
    setSpinning(true);
    setLoading(true);
    setError('');
    setStatusType('loading');
    setAllData([]);
    try {
      const res = await api.get(`/bhadra?month=${m}&year=${y}`);
      if (!res.data.success) throw new Error(res.data.message || 'Unknown error');
      setAllData(res.data.bhadra || []);
      setStatusType('success');
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || 'Failed to fetch';
      setError(msg);
      setStatusType('error');
    } finally {
      setLoading(false);
      setSpinning(false);
    }
  }, []);

  useEffect(() => { fetchBhadra(month, year); }, []); // eslint-disable-line

  const handleLoad = () => fetchBhadra(month, year);

  const monthLabel  = MONTH_NAMES[month - 1];
  const statusColor =
    statusType === 'loading' ? '#f59e0b'
    : statusType === 'success' ? '#10b981'
    : statusType === 'error'   ? '#ef4444'
    : 'rgba(255,255,255,0.4)';

  const statusText =
    statusType === 'loading' ? `Loading Time Trend Shift periods for ${monthLabel} ${year}…`
    : statusType === 'error'  ? `Error: ${error}`
    : statusType === 'success'
      ? `${displayed.length} Time Trend Shift period${displayed.length !== 1 ? 's' : ''} for ${monthLabel} ${year}${tradingOnly ? ' · Trading days only' : ''} · All times IST (UTC+5:30)`
    : 'Select a month and year, then load.';

  const totalHrs     = allData.reduce((a, b) => a + (b.durationHrs || 0) + (b.durationMins || 0) / 60, 0);
  const tradingCount = allData.filter((b) => isTradingDay(b.start)).length;

  return (
    <AppLayout pageBadge="Time Trend Shift">

      {/* ── Starfield ── */}
      <div style={S.starfield}>
        {STARS.map((s) => (
          <div key={s.id} style={{
            ...S.star,
            left: s.left, top: s.top,
            animationDelay: s.delay,
            width: s.size, height: s.size,
          }} />
        ))}
      </div>

      {/* ── No-scroll shell ── */}
      <div className="tts-shell" style={{ ...S.shell, height: `calc(100vh - ${HEADER_H}px)` }}>

        {/* ══ ROW 1 — Controls bar ════════════════════════════════ */}
        <div className="tts-controls-bar" style={S.controlsBar}>
          <div className="tts-bar-left" style={S.barLeft}>
            <span style={{ fontSize: '1.3rem' }}>🕉️</span>
            <span className="tts-page-title" style={S.pageTitle}>Time Trend Shift</span>
            <span className="tts-page-sub" style={S.pageSub}>Lunar Inauspicious Window Tracker</span>
          </div>
          <div className="tts-bar-right" style={S.barRight}>
            <div className="tts-select-wrap" style={S.selWrap}>
              <select className="tts-select" value={month} onChange={(e) => setMonth(Number(e.target.value))} style={S.sel}>
                {MONTH_NAMES.map((n, i) => <option key={i+1} value={i+1} style={S.selOption}>{n}</option>)}
              </select>
              <span className="tts-select-arrow" style={S.selArrow}>▾</span>
            </div>
            <div className="tts-select-wrap" style={S.selWrap}>
              <select className="tts-select" value={year} onChange={(e) => setYear(Number(e.target.value))} style={S.sel}>
                {YEAR_OPTIONS.map((y) => <option key={y} value={y} style={S.selOption}>{y}</option>)}
              </select>
              <span className="tts-select-arrow" style={S.selArrow}>▾</span>
            </div>
            <div
              className="tts-toggle"
              style={{ ...S.toggleTrack, background: tradingOnly ? 'rgba(220,38,38,0.25)' : 'rgba(255,255,255,0.06)' }}
              onClick={() => setTradingOnly((p) => !p)}
              title="Toggle trading days only"
            >
              <div className="tts-toggle-thumb" style={{ ...S.toggleThumb, left: tradingOnly ? 'calc(100% - 26px)' : 3 }} />
              <span className="tts-toggle-label" style={{ ...S.toggleLabel, paddingLeft: tradingOnly ? 10 : 28, color: tradingOnly ? '#fff' : 'rgba(255,255,255,0.45)' }}>
                {tradingOnly ? 'Mon–Fri' : 'All Days'}
              </span>
            </div>
            <button className="tts-load-btn" onClick={handleLoad} disabled={spinning} style={S.btnLoad}>
              <span style={spinning ? { display: 'inline-block', animation: 'spin 1s linear infinite' } : undefined}>🔍</span>
              &nbsp;Load
            </button>
          </div>
        </div>

        {/* ══ ROW 2 — Stats + Info strip ══════════════════════════ */}
        <div className="tts-info-row" style={S.infoRow}>
          <div className="tts-pills-row" style={S.pillsRow}>
            <InfoPill icon="🌙" value={allData.length}          label="Total"        color="#dc2626" />
            <InfoPill icon="📅" value={tradingCount}            label="Trading Days" color="#f59e0b" />
            <InfoPill icon="⏱"  value={`${totalHrs.toFixed(1)}h`} label="Total Duration" color="#8b5cf6" />
            <InfoPill icon="✅" value={displayed.length}        label="Showing"      color="#10b981" />
          </div>
          <button
            type="button"
            className="tts-about-btn"
            style={S.aboutBtn}
            onClick={() => setShowAbout((prev) => !prev)}
            aria-expanded={showAbout}
            aria-label="Toggle Time Trend Shift details"
            title="About Time Trend Shift"
          >
            ℹ
          </button>
          <div
            className="tts-about-chip"
            style={{ ...S.aboutChip, display: showAbout ? 'flex' : 'none' }}
          >
            <span className="tts-about-text" style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.5 }}>
              A Time Trend Shift marks an inauspicious astrological window arising from a specific Moon-Sun
              alignment, traditionally avoided for new ventures, journeys, and important decisions. It recurs
              seven times within each lunar month, with each window spanning roughly 6 degrees of Moon-Sun elongation.
              All times in <strong style={{ color: 'rgba(255,255,255,0.85)' }}>IST (UTC+5:30)</strong>.
            </span>
          </div>
        </div>

        {/* ══ ROW 3 — Status bar ══════════════════════════════════ */}
        <div className="tts-status-bar" style={{ ...S.statusBar, color: statusColor, borderColor: statusColor + '33' }}>
          {statusText}
        </div>

        {/* ══ ROW 4 — Cards (fills remaining height, scrolls inside) */}
        <div className="tts-cards-area" style={S.cardsArea}>
          {loading && !allData.length ? (
            <div style={S.centeredMsg}>
              <div style={S.loaderRing} />
              <p style={{ marginTop: 16, color: 'rgba(255,255,255,0.4)', fontSize: '0.82rem' }}>
                Fetching Time Trend Shift periods…
              </p>
            </div>
          ) : error && !allData.length ? (
            <div style={S.centeredMsg}>
              <div style={{ fontSize: '2.5rem', opacity: 0.35 }}>⚠️</div>
              <p style={{ marginTop: 12, color: '#fca5a5', fontSize: '0.82rem' }}>{error}</p>
            </div>
          ) : displayed.length === 0 && statusType === 'success' ? (
            <div style={S.centeredMsg}>
              <div style={{ fontSize: '2.5rem', opacity: 0.3 }}>✅</div>
              <p style={{ marginTop: 12, color: 'rgba(255,255,255,0.4)', fontSize: '0.82rem' }}>
                No Time Trend Shift periods{tradingOnly ? ' on trading days' : ''} for this month.
              </p>
            </div>
          ) : (
            <div className="tts-cards-grid" style={S.cardsGrid}>
              {displayed.map((item, i) => <BhadraCard key={i} item={item} index={i} />)}
            </div>
          )}
        </div>

      </div>{/* /shell */}
    </AppLayout>
  );
}

/* ─── Styles ─────────────────────────────────────────────────────── */
const S = {
  /* starfield */
  starfield: {
    position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', overflow: 'hidden',
    background: 'linear-gradient(135deg,#1a0a0a 0%,#16213e 50%,#0f0a1e 100%)',
  },
  star: {
    position: 'absolute', background: '#fff', borderRadius: '50%',
    animation: 'twinkle 3s infinite',
  },

  /* no-scroll shell */
  shell: {
    position: 'relative', zIndex: 1,
    display: 'flex', flexDirection: 'column',
    overflow: 'hidden',
    padding: '10px 14px 12px',
    marginTop: HEADER_H,
    gap: 8,
    color: '#fff',
    fontFamily: "'Poppins','Segoe UI',sans-serif",
  },

  /* ── Row 1: Controls ── */
  controlsBar: {
    flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    flexWrap: 'wrap', gap: 10,
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 12,
    padding: '10px 16px',
  },
  barLeft:  { display: 'flex', alignItems: 'center', gap: 10 },
  barRight: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  pageTitle: { fontSize: '1rem', fontWeight: 700, color: '#fff' },
  pageSub:   { fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)' },

  selWrap: { position: 'relative' },
  sel: {
    appearance: 'none',
    padding: '7px 30px 7px 12px',
    fontSize: '0.82rem', borderRadius: 8,
    border: '1px solid rgba(255,255,255,0.2)',
    background: '#1e2340', color: '#fff',
    cursor: 'pointer', minWidth: 120, outline: 'none',
    fontFamily: 'inherit',
  },
  selOption: {
    background: '#1e2340', color: '#fff',
  },
  selArrow: {
    position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
    color: 'rgba(255,255,255,0.4)', pointerEvents: 'none', fontSize: '0.75rem',
  },

  toggleTrack: {
    position: 'relative', height: 30, width: 90,
    borderRadius: 15, cursor: 'pointer',
    border: '1px solid rgba(255,255,255,0.12)',
    display: 'flex', alignItems: 'center',
    overflow: 'hidden', flexShrink: 0,
    transition: 'background 0.3s',
  },
  toggleThumb: {
    position: 'absolute', top: 3,
    width: 24, height: 24,
    background: 'linear-gradient(135deg,#dc2626,#991b1b)',
    borderRadius: '50%',
    transition: 'left 0.25s', zIndex: 0,
    boxShadow: '0 2px 6px rgba(220,38,38,0.5)',
  },
  toggleLabel: {
    position: 'relative', zIndex: 1,
    fontSize: '0.63rem', fontWeight: 600, letterSpacing: 0.3,
    pointerEvents: 'none', userSelect: 'none',
    transition: 'color 0.3s, padding-left 0.25s',
    whiteSpace: 'nowrap',
  },

  btnLoad: {
    background: 'linear-gradient(135deg,#dc2626,#991b1b)',
    border: 'none', borderRadius: 8,
    color: '#fff', padding: '7px 18px',
    fontSize: '0.82rem', fontWeight: 600,
    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0,
  },

  /* ── Row 2: Info strip ── */
  infoRow: {
    flexShrink: 0,
    display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10,
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: 12,
    padding: '10px 16px',
  },
  pillsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'nowrap',
    minWidth: 0,
    flex: 1,
  },
  pill: {
    display: 'flex', alignItems: 'center', gap: 8,
    border: '1px solid', borderRadius: 10,
    padding: '6px 14px', background: 'rgba(255,255,255,0.04)', flexShrink: 0,
  },
  pillDot: {
    width: 28, height: 28, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '0.9rem', flexShrink: 0,
  },
  aboutBtn: {
    border: '1px solid rgba(249,115,22,0.5)',
    background: 'rgba(249,115,22,0.18)',
    color: '#fdba74',
    width: 28,
    height: 28,
    borderRadius: '50%',
    fontSize: '0.9rem',
    fontWeight: 700,
    cursor: 'pointer',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
  },
  aboutChip: {
    width: '100%',
    display: 'none',
    alignItems: 'flex-start',
    gap: 8,
    background: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 10,
    padding: '8px 10px',
  },

  /* ── Row 3: Status ── */
  statusBar: {
    flexShrink: 0,
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid', borderRadius: 8,
    padding: '7px 14px', fontSize: '0.8rem', textAlign: 'center',
    transition: 'color 0.3s, border-color 0.3s',
  },

  /* ── Row 4: Cards area ── */
  cardsArea: {
    flex: 1, minHeight: 0,
    overflowY: 'auto', overflowX: 'hidden',
    borderRadius: 12,
  },
  cardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))',
    gap: '10px',
    padding: '4px 2px 8px',
  },
  centeredMsg: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    height: '100%', minHeight: 200,
  },
  loaderRing: {
    width: 44, height: 44, borderRadius: '50%',
    border: '3px solid rgba(220,38,38,0.2)',
    borderTopColor: '#dc2626',
    animation: 'spin 0.9s linear infinite',
  },

  /* ── BhadraCard ── */
  card: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: 14, padding: '12px 14px',
    backdropFilter: 'blur(8px)',
    boxShadow: '0 2px 12px rgba(0,0,0,0.25)',
    transition: 'transform 0.2s, border-color 0.2s, box-shadow 0.2s',
  },
  cardTop: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 10, paddingBottom: 10,
    borderBottom: '1px solid rgba(255,255,255,0.07)',
  },
  cardBadge: {
    background: 'linear-gradient(135deg,#dc2626,#991b1b)',
    color: '#fff', padding: '3px 12px',
    borderRadius: 20, fontSize: '0.75rem', fontWeight: 600,
  },
  cardDur: { fontSize: '0.75rem', color: 'rgba(255,255,255,0.55)' },
  timeSplit: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' },
  timeBlock: { padding: '8px 10px', borderRadius: 8, background: 'rgba(0,0,0,0.2)' },
  timeLabel: {
    fontSize: '0.63rem', textTransform: 'uppercase', letterSpacing: 1,
    color: 'rgba(255,255,255,0.38)', marginBottom: 5,
    display: 'flex', alignItems: 'center', gap: 4,
  },
  timeVal: { color: '#fff', fontSize: '0.8rem', fontWeight: 500, lineHeight: 1.4 },
  contBadge: {
    display: 'inline-block',
    background: 'rgba(249,115,22,0.14)', color: '#f97316',
    padding: '2px 7px', borderRadius: 4,
    fontSize: '0.63rem', marginTop: 5, fontStyle: 'italic',
  },
};
